import { Component, Input, OnChanges, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { takeUntil, first, skip } from 'rxjs/operators';
import { ReplaySubject, Subject } from 'rxjs';
import { ProjectElement } from '../../resources/element/project-element.interface';
import { ElementService } from '../../resources/element/element.service';
import { ModalService } from '../../services/modal/modal.service';
import { User } from '../../resources/user/user.interface';
import { UserService } from '../../resources/user/user.service';

/**
 * User icon.
 *
 * The user icon has 2 options, and it's best to use *only one*:
 *  - Element: Linked to an element. Updated *automatically* when changing the elements user.
 *  - User: Just displays a user from a userId. Updated *manually* by setting a new value.
 */
@Component({
  selector: 'sg2-user-icon',
  templateUrl: './user-icon.component.html',
  styleUrls: ['./user-icon.component.less']
})
export class UserIconComponent implements OnChanges, OnDestroy, OnInit {

  /** Default icon. */
  readonly DEFAULT_INITIALS = '?';

  /** Optional animate.css loading animation. */
  @Input() animated: string;

  /**
   * Connect to an element so it changes when the element
   * changes. Prevent added logic in controller/template.
   *
   * @note Use this to display an element's assigned user:
   *  - Updates upon assigning from element
   *  - Assign new element user from avatar's options menu
   */
  @Input() element: ProjectElement;

  /**
   * User input, can be either User object or User id.
   * Prevents if/else/ternary logic inside templates, or
   * the need to inject UserService in parent controller.
   *
   * @note Use this to just display a user:
   *  - Comments
   *  - User management
   */
  @Input() user: User | User['_id'];

  /** Destroy hook. */
  private destroy$ = new Subject<any>();

  /** Current displayed user. */
  public user$: ReplaySubject<User> = new ReplaySubject();

  /** List of all users, to display the dropdown. */
  public users: User[];

  /** Initials. */
  public initials: string;


  constructor(
    private elementService: ElementService,
    private modalService: ModalService,
    private userService: UserService
  ) {
    this._trackInitials();
  }

  /**
   * Show warning when both Input's are used simultaneously.
   */
  ngOnInit() {
    if (this.user && this.element) {
      console.warn('UserInputComponent: [user] and [element] should not be user concurrently.');
    }
    if (this.element) {
      this._trackElement();
      this.userService.user$.pipe(first()).subscribe(u => {
        this.users = u;
        this._setUser();
      });
    }
  }

  /**
   * Update user icon when the user input changes.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (this.user) {
      this._setUser();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Assign user to an element.
   *
   * @param element The element to assign the user to.
   * @param user The user (id) to assign to the element.
   */
  public assignToUser(element: ProjectElement, user: User['_id']): void {
    this.modalService.openAssignTo(element, { user: user }).afterClosed().subscribe(settings => {
      if (settings) {
        this.elementService.assignToWithOptions(element, settings);
      }
    });
  }

  /**
   * Set user as observable.
   *
   * Create either an observable from the user object binding, or
   * get user from userService when the input is only an id.
   */
  private _setUser(): void {
    if (typeof this.user === 'object') {
      this.user$.next(this.user);
    } else {
      const userId = this.user || this.element && this.element.assignedToUserId;
      if (userId !== -1) {
        this.user$.next(this.userService.getUser(userId));
      }
    }
  }

  /**
   * Always have latest version of the element.
   */
  private _trackElement(): void {
    this.elementService.element$.pipe(
      skip(1),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.element = this.elementService.getElement(this.element._id);
      this._setUser();
    });
  }

  /**
   * Set user initials.
   *
   * Subscribe to user$ to detect changes, and update initials.
   */
  private _trackInitials(): void {
    this.user$.subscribe(user => {
      this.initials = user && (user.nameFirst[0] + user.nameLast[0]).toUpperCase();
    });
  }
}
